export const LIT_PREFIX = 'lit_'

/**
 * Centralized version for Lit Actions and derived key memos.
 * This version is used to scope wrapped keys and runner secrets.
 *
 * IMPORTANT: Incrementing this version will:
 * - Generate new root keys, runner secrets, and Namada keys for all users
 * - Previous keys will no longer be found (memo mismatch)
 *
 * Use cases for incrementing:
 * - Breaking changes to key derivation logic
 * - Security-related key rotation
 * - Testing new derivation schemes
 *
 * This value is served via lit-server's /lit/config endpoint to the Go backend.
 * Should match GeneratedKeyVersion from constants.go.
 */
export const LIT_ACTIONS_VERSION = 'v8.703'

/**
 * Versioned memo prefixes for identifying versioned memos without the version suffix.
 */
export const VERSIONED_MEMO_PREFIXES = {
	rootKey: 'polaris-root-derivation-key-scoped',
	namadaWrappedKey: 'polaris-wallet-namada-from-root-scoped',
	derivedRunnerSecret: 'polaris-runner-secret-from-root-scoped',
} as const

/**
 * Versioned memos for wrapped key identification.
 * These memos are used to look up and identify wrapped keys stored in Lit Protocol.
 * The version suffix ensures keys are scoped to a specific derivation scheme.
 */
export const MEMOS = {
	/** Prefix for root derivation key memos */
	rootKeyPrefix: VERSIONED_MEMO_PREFIXES.rootKey,
	/** Prefix for Namada wrapped key memos */
	namadaWrappedKeyPrefix: VERSIONED_MEMO_PREFIXES.namadaWrappedKey,
	/** Prefix for runner secret memos */
	derivedRunnerSecretPrefix: VERSIONED_MEMO_PREFIXES.derivedRunnerSecret,
	/** Memo for root derivation key (versioned) */
	rootKey: `${VERSIONED_MEMO_PREFIXES.rootKey}-${LIT_ACTIONS_VERSION}`,
	/** Memo for Namada wrapped key derived from root (versioned) */
	namadaWrappedKey: `${VERSIONED_MEMO_PREFIXES.namadaWrappedKey}-${LIT_ACTIONS_VERSION}`,
	/** Memo for runner secret derived from root (versioned) */
	derivedRunnerSecret: `${VERSIONED_MEMO_PREFIXES.derivedRunnerSecret}-${LIT_ACTIONS_VERSION}`,
	/** Memo for K256 wrapped key (static) */
	k256WrappedKey: 'polaris-wallet-k256',
	/** Memo for ED25519 wrapped key (static) */
	ed25519WrappedKey: 'polaris-wallet-ed25519',
	/** Memo for legacy runner secret (static) */
	runnerSecret: 'polaris-runner-secret',
	/** Memo for vault key from mnemonic (static) */
	vaultKeyFromMnemonic: 'polaris-vault-key-from-mnemonic',
	/** Prefix for polaris wallet memos */
	polarisWalletPrefix: 'polaris-wallet',
} as const

/**
 * Domain prefix for privacy-preserving swap key derivation
 * Used in: Hash(PRIVACY_SWAP_DOMAIN_PREFIX + DOMAIN_SEPARATOR + runnerSecret + DOMAIN_SEPARATOR + routeId)
 */
export const PRIVACY_SWAP_DOMAIN_PREFIX = 'PRIVACY_SWAP'

/**
 * Domain prefix for root key hierarchy
 * Used as the top-level identifier in hierarchical key derivation
 */
export const ROOT_KEY_DOMAIN_PREFIX = 'ROOT_KEY'

/**
 * Domain prefix for vault key derivation
 * Used in: Hash(ROOT_KEY_DOMAIN_PREFIX + DOMAIN_SEPARATOR + VAULT_KEY_DOMAIN_PREFIX + DOMAIN_SEPARATOR + chain + ...)
 */
export const VAULT_KEY_DOMAIN_PREFIX = 'VAULT'

/**
 * Domain prefix for runner parent key derivation
 * Used in: Hash(ROOT_KEY_DOMAIN_PREFIX + DOMAIN_SEPARATOR + RUNNER_PARENT_KEY_DOMAIN_PREFIX + ...)
 */
export const RUNNER_PARENT_KEY_DOMAIN_PREFIX = 'RUNNER'

/**
 * Separator used in domain-separated key derivation
 */
export const DOMAIN_SEPARATOR = '|'

/**
 * Version for Namada vault key derivation
 * Increment when changing the derivation scheme to generate different keys
 * The main purpose is to update the keys during testing. Lit does not allow uploading
 * keys with the same dataToEncryptHash. As a result, during implementation and testing,
 * we bump up the version to generate different keys for retesting.
 * IMPORTANT: In production, be extremely careful when touching this. Updating this version
 * would change vault keys for all users.
 */
export const NAMADA_VAULT_DERIVATION_VERSION = 'v3'

export const NAMADA_KEY_TYPE = 'namada-derived' as const

/**
 * Supported key types for wrapped key derivation
 */
export const KEY_TYPE = {
	K256: 'K256',
	ED25519: 'ed25519',
	NAMADA: NAMADA_KEY_TYPE,
} as const
